import type { ApiCartException } from '../../exceptions/exceptionDefinitions';
import type { HybrisCart } from '../../models/hybris/hybrisCart';
import type { HybrisCartModification } from '../../models/hybris/hybrisCartModification';
import type { HybrisPaymentModes } from '../../models/hybris/hybrisPaymentModeData';
import type { HybrisSwitchStore } from '../../models/hybris/hybrisSwitchStore';
import type { HybrisVoucher } from '../../models/hybris/hybrisVoucher';
import type { ApiAppliedVouchers, ApiPaymentMethods } from './responses/cartResponses';

import { appConfig } from '../../../../appConfig';
import { HybrisResponseFieldsType } from '../../models/hybris/hybrisConstants';
import getDirectHybrisAxiosClient, {
    getUserIdUrlSegment,
} from '../clients/directHybrisAxiosClient';
import {
    cartVoucherErrorInterceptor,
    updateItemQuantityInterceptor,
} from './cartApiErrorInterceptors';

const userIdSegment = (userId: string) => getUserIdUrlSegment(userId);
const encodedUserIdSegment = (userId: string) => encodeURIComponent(userIdSegment(userId));

const directCartApi = {
    addToCartFromOrder:
        (token: string) =>
        async (userId: string, cartId: string, orderId: string): Promise<HybrisCart> => {
            const response = await getDirectHybrisAxiosClient(token).post(
                `users/${userIdSegment(userId)}/carts/${cartId}/addfromorder/${orderId}`,
            );
            const resp = response.data;
            return resp;
        },

    updateCartItemOld:
        (token?: string) =>
        async (
            userId: string,
            cartId: string,
            item: {
                productId: string;
                variantId: string | undefined;
                quantity: number;
                itemReplaceable?: boolean;
            },
        ) => {
            const queryString = new URLSearchParams();
            queryString.set('qty', item.quantity.toString());
            queryString.set('code', item.productId);
            if (item.variantId) {
                queryString.set('varianceCode', item.variantId);
            }
            if (item.itemReplaceable !== undefined) {
                queryString.set('replaceable', item.itemReplaceable ? 'true' : 'false');
            }

            const response = await getDirectHybrisAxiosClient(token).post<HybrisCartModification>(
                `users/${encodedUserIdSegment(userId)}/carts/${encodeURIComponent(
                    cartId,
                )}/products?${queryString.toString()}`,
            );

            const storedLineQuantity = response.data.quantity ?? response.data.entry?.quantity;
            const isAcceptedLowStock =
                response.data.statusCode === 'lowStock' && storedLineQuantity > 0;

            if (response.data.statusCode !== 'success' && !isAcceptedLowStock) {
                let errorMessage = appConfig.coopSettings.ecommerce.errorMessage.general;
                switch (response.data.statusCode) {
                    case 'restrictedByUserGroupAndCategory':
                        errorMessage = appConfig.coopUserSettings.punchout.isPunchout
                            ? appConfig.coopSettings.ecommerce.errorMessage
                                  .productRestrictedForBusinessUnit
                            : appConfig.coopSettings.ecommerce.errorMessage
                                  .productRestrictedForPrivateUser;
                        break;
                    case 'unavailable':
                        errorMessage =
                            appConfig.coopSettings.ecommerce.errorMessage.productNotFound;
                        break;
                    case 'foodBagOnly':
                        break;
                    default:
                        break;
                }
                throw <ApiCartException>{ code: null, friendlyMessage: errorMessage, type: 'Cart' };
            }
            return response.data;
        },
    updateCartItem:
        (token?: string) =>
        async (
            userId: string,
            cartId: string,
            item: {
                productId: string;
                variantId: string | undefined;
                quantity: number;
                itemReplaceable?: boolean;
            },
        ) => {
            const queryString = new URLSearchParams();
            queryString.set('qty', item.quantity.toString());
            queryString.set('code', item.productId);
            if (item.variantId) {
                queryString.set('varianceCode', item.variantId);
            }
            if (item.itemReplaceable !== undefined) {
                queryString.set('replaceable', item.itemReplaceable ? 'true' : 'false');
            }

            queryString.set('fields', 'FULL');

            const response = await getDirectHybrisAxiosClient(token, [
                updateItemQuantityInterceptor,
            ]).post<HybrisCart>(
                `users/${encodedUserIdSegment(userId)}/cartdata/${encodeURIComponent(
                    cartId,
                )}/products?${queryString.toString()}`,
            );

            return response.data;
        },
    bulkEntries:
        (token?: string) =>
        async (
            userId: string,
            cartId: string,
            items: { productCode: string; varianceCode: string | undefined; quantity: number }[],
        ) => {
            const response = await getDirectHybrisAxiosClient(token).post<{
                CartModifications: {}[];
            }>(`users/${userIdSegment(userId)}/carts/${cartId}/bulkentries`, items);
            const resp = response.data.CartModifications;
            return resp;
        },

    createCart: async (shoppingUserId: string, token?: string) => {
        const response = await getDirectHybrisAxiosClient(token).post<HybrisCart>(
            `users/${encodedUserIdSegment(shoppingUserId)}/carts`,
        );

        return response.data;
    },
    getCart: async (shoppingUserId: string, cartId: string, token?: string) => {
        const response = await getDirectHybrisAxiosClient(token).get<HybrisCart>(
            `users/${encodedUserIdSegment(shoppingUserId)}/carts/${encodeURIComponent(
                cartId,
            )}?fields=${HybrisResponseFieldsType.Full}`,
        );

        return response.data;
    },
    initializeCheckout: async (shoppingUserId: string, cartId: string, token?: string) => {
        const response = await getDirectHybrisAxiosClient(token).post<HybrisCart>(
            `users/${encodedUserIdSegment(shoppingUserId)}/carts/${encodeURIComponent(
                cartId,
            )}/checkout?fields=${HybrisResponseFieldsType.Basic}`,
        );

        return response.data;
    },
    deleteCart: async (shoppingUserId: string, cartId: string, token?: string) => {
        const response = await getDirectHybrisAxiosClient(token).delete<HybrisCart>(
            `users/${encodedUserIdSegment(shoppingUserId)}/carts/${encodeURIComponent(cartId)}`,
        );

        return response.data;
    },
    emptyCart: (shoppingUserId: string, cartId: string, token?: string) =>
        getDirectHybrisAxiosClient(token).put<HybrisCart>(
            `users/${encodedUserIdSegment(shoppingUserId)}/carts/${encodeURIComponent(cartId)}?emptyCart=true`,
        ),
    mergeCarts: async (
        shoppingUserId: string,
        fromCartId: string,
        toCartId: string,
        token: string,
    ) => {
        const response = await getDirectHybrisAxiosClient(token).post<HybrisCart>(
            `users/${encodedUserIdSegment(shoppingUserId)}/carts?fields=${HybrisResponseFieldsType.Full}&oldCartId=${encodeURIComponent(
                fromCartId,
            )}&toMergeCartGuid=${encodeURIComponent(toCartId)}`,
        );

        return response.data;
    },
    changePostCode: async (
        shoppingUserId: string,
        cartId: string,
        postCode: string,
        confirmed: boolean,
        baseStoreId?: string,
        token?: string,
    ) => {
        const queryString = new URLSearchParams();
        queryString.set('postCode', postCode);
        queryString.set('confirmed', confirmed ? 'true' : 'false');
        if (baseStoreId) {
            queryString.set('basestoreCode', baseStoreId);
        }

        const response = await getDirectHybrisAxiosClient(token).put<HybrisSwitchStore>(
            `users/${encodedUserIdSegment(
                shoppingUserId,
            )}/carts/${cartId}/postcode?${queryString.toString()}`,
        );
        // if we change to the already current store in the cart, we get 200ok with no response, thats why i added updateresult == null as success :E
        return response.data;
    },
    changePointOfService: async (
        shoppingUserId: string,
        cartId: string,
        pickupPointId: string,
        confirmed: boolean,
        token?: string,
    ) => {
        const queryString = new URLSearchParams();
        queryString.set('posName', pickupPointId);
        queryString.set('confirmed', confirmed ? 'true' : 'false');

        const response = await getDirectHybrisAxiosClient(token).put<HybrisSwitchStore>(
            `users/${encodedUserIdSegment(
                shoppingUserId,
            )}/carts/${cartId}/pointofservice?${queryString.toString()}`,
        );
        return response.data;
    },
    setDeliveryMode: async (
        shoppingUserId: string,
        cartId: string,
        deliveryModeId: string,
        token?: string,
    ) => {
        const queryString = new URLSearchParams();
        queryString.set('deliveryModeId', deliveryModeId);

        await getDirectHybrisAxiosClient(token).put(
            `users/${encodedUserIdSegment(
                shoppingUserId,
            )}/carts/${cartId}/deliverymode?${queryString.toString()}`,
        );
    },

    applyVoucher: async (options: {
        shoppingUserId: string;
        voucherId: string;
        token?: string;
    }): Promise<ApiAppliedVouchers | undefined> => {
        const vouchers = await getDirectHybrisAxiosClient(options.token, [
            cartVoucherErrorInterceptor,
        ]).post<{ vouchers: HybrisVoucher[] }>(
            `users/${encodedUserIdSegment(
                options.shoppingUserId,
            )}/carts/current/vouchers?voucherId=${encodeURIComponent(options.voucherId)}`,
        );

        const hybrisVoucher = vouchers.data.vouchers.find(
            (v) => v.voucherCode === options.voucherId,
        );
        if (hybrisVoucher) {
            return {
                ...hybrisVoucher,
                priceData: {
                    inclTaxPrice: hybrisVoucher.value,
                },
            };
        }
        return undefined;
    },

    removeVoucher: async (options: {
        shoppingUserId: string;
        voucherId: string;
        token?: string;
    }): Promise<void> => {
        await getDirectHybrisAxiosClient(options.token).delete(
            `users/${encodedUserIdSegment(
                options.shoppingUserId,
            )}/carts/current/vouchers/${encodeURIComponent(options.voucherId)}`,
        );
    },
    payment: {
        list: async (options: {
            shoppingUserId: string;
            token: string;
        }): Promise<ApiPaymentMethods> => {
            const response = await getDirectHybrisAxiosClient(
                options.token,
            ).get<HybrisPaymentModes>(
                `users/${encodedUserIdSegment(options.shoppingUserId)}/carts/current/paymentmethods`,
            );

            return response.data?.paymentModes;
        },
    },
    deactivateECoupon: async (
        shoppingUserId: string,
        cartId: string,
        ecouponId: string,
        token: string,
    ) => {
        const response = await getDirectHybrisAxiosClient(token).delete<
            Pick<HybrisCart, 'ecoupons'>
        >(
            `users/${encodedUserIdSegment(shoppingUserId)}/carts/${encodeURIComponent(
                cartId,
            )}/ecoupons/${encodeURIComponent(ecouponId)}?fields=${HybrisResponseFieldsType.Basic}`,
        );

        return response.data;
    },
    activateECoupon: async (
        shoppingUserId: string,
        cartId: string,
        ecouponId: string,
        token: string,
    ) => {
        const response = await getDirectHybrisAxiosClient(token).put<Pick<HybrisCart, 'ecoupons'>>(
            `users/${encodedUserIdSegment(shoppingUserId)}/carts/${encodeURIComponent(
                cartId,
            )}/ecoupons/${encodeURIComponent(ecouponId)}?fields=${HybrisResponseFieldsType.Basic}`,
        );

        return response.data;
    },
};

export default directCartApi;
